/**
 * Putting a price on the market's grid, the right way round.
 *
 * Which way an off-step price resolves is not cosmetic: the exchange rejects a
 * price that is not a multiple of `priceStep` (PriceNotOnStep, after the trader
 * has already signed), and resolving it the wrong way makes them pay more than
 * they typed. A BUY floors and a SELL ceils, always — including when the order
 * is a CLOSE, where the side that trades is the opposite of the side held.
 *
 * Two forms of the same rule live here because the order path has two: decimal
 * text for the form and the API, and 1e18 `bigint` units for the contract call,
 * which must agree to the wei.
 */
import { bestLevel, BookLevels } from "./book";
import { Dec, dec } from "./decimal";

/** Which way an off-grid price is resolved. */
export type PriceDirection = "floor" | "ceil";

/** A buy never pays above the price it asked for; a sell never sells below. */
export const orderPriceDirection = (side: "buy" | "sell"): PriceDirection =>
  side === "buy" ? "floor" : "ceil";

/**
 * The direction for a price that CLOSES `positionSide`. Closing a long sells,
 * so it ceils; closing a short buys, so it floors.
 */
export const closePriceDirection = (
  positionSide: "buy" | "sell"
): PriceDirection => orderPriceDirection(positionSide === "buy" ? "sell" : "buy");

/**
 * `v` on the `step` grid. A step that cannot be read leaves the price alone —
 * a grid we do not know is not a reason to move the trader's price.
 */
export const alignPrice = (v: Dec, step: Dec, dir: PriceDirection): Dec => {
  const price = dec.parse(v);
  if (price === null) return "0";
  return dec.toStep(price, step, dir) ?? price;
};

/**
 * The same alignment in raw 1e18 units, for the contract path.
 *
 * Ported from `alignRawPriceToStep`: a non-positive price or an unusable step
 * passes through untouched, so this can sit in front of every order without
 * having to know whether the on-chain specs resolved.
 */
export const alignRawPrice = (
  raw: bigint,
  stepRaw: bigint,
  dir: PriceDirection
): bigint => {
  if (stepRaw <= 0n || raw <= 0n) return raw;
  const remainder = raw % stepRaw;
  if (remainder === 0n) return raw;
  const floored = raw - remainder;
  return dir === "floor" ? floored : floored + stepRaw;
};

export interface BookRefPrices {
  /** Best bid, exactly as the book quotes it. */
  bid?: Dec;
  /** Best ask, exactly as the book quotes it. */
  ask?: Dec;
  /** A reference price on the grid, or absent when none can be had. */
  mid?: Dec;
  /** Where `mid` came from. */
  source: "book" | "mark" | "none";
}

/**
 * A reference price to seed a limit field or preview a market order.
 *
 * The mid is only a mid when the book quotes BOTH sides — half of a one-sided
 * book is not a mid — so anything else falls back to the mark. `bid` and `ask`
 * are still reported whenever they exist, even when the mid came from the mark,
 * because the touch is what a post-only price is set against.
 *
 * `source: "none"` means neither the book nor the mark could price this market:
 * the caller shows its unknown state rather than seeding the field with 0.
 */
export const bookRefPrices = (
  book: { bids?: BookLevels; asks?: BookLevels } | null | undefined,
  mark: Dec | null | undefined,
  step: Dec,
  dir: PriceDirection
): BookRefPrices => {
  const bid = bestLevel(book?.bids, "bids");
  const ask = bestLevel(book?.asks, "asks");
  const prices = {
    bid: bid?.price,
    ask: ask?.price,
  };

  if (bid && ask) {
    const rawMid = dec.div(dec.add(bid.price, ask.price), "2", 40, "floor");
    if (rawMid !== null) {
      return { ...prices, mid: alignPrice(rawMid, step, dir), source: "book" };
    }
  }

  const markPrice = dec.parse(mark ?? null);
  if (markPrice !== null && dec.gt(markPrice, "0")) {
    return { ...prices, mid: alignPrice(markPrice, step, dir), source: "mark" };
  }

  return { ...prices, mid: undefined, source: "none" };
};
